
import { defineComponent, ref, onMounted, watch, computed } from 'vue'
import LoadingButton from '@/views/components/LoadingButton.vue'
import ContentLayout from '@/views/layout/ContentLayout.vue'
import ResponseResultAlert from '@/views/components/ResponseResultAlert.vue'
import { object } from 'yup'
import { myValidationSchemaObj } from '@/views/validation'
import { useForm } from 'vee-validate'
import { randomString } from '@/views/utils'
import { FacilityInfoUiItem, repository } from '@/repositories/commons/corporationInfoRepository'
import { callApiWrapper } from '@/views/callApiWrapper'
import { prefectures, corporationTypes } from '@/views/corporation-info/selectOptions'
import { corporationTypeType, CorporationTypeType } from '@/views/corporation-info/types'
import FacilityInfoItem from '@/views/corporation-info/FacilityInfoItem.vue'

type InputForm = {
  prefectureNumber: string;
  medicalCorporationNumber: string;
  corporationNumber: string;
  corporateName: string;
  location: string;
  corporationType: CorporationTypeType;
}

const validationSchema = object({
  prefectureNumber: myValidationSchemaObj.requiredString,
  medicalCorporationNumber: myValidationSchemaObj.fixedLengthNumber(5),
  corporationNumber: myValidationSchemaObj.fixedLengthNumber(13),
  corporateName: myValidationSchemaObj.maxString(50),
  location: myValidationSchemaObj.maxString(100),
  corporationType: myValidationSchemaObj.requiredString
})

export default defineComponent({
  components: {
    FacilityInfoItem,
    ResponseResultAlert,
    ContentLayout,
    LoadingButton
  },
  setup () {
    const requestError = ref<string | null>(null)
    const loading = ref(false)
    const isSucceedRequest = ref(false)
    const { errors, values, meta, validate } = useForm<InputForm>({
      validationSchema: validationSchema,
      initialValues: {
        prefectureNumber: '',
        medicalCorporationNumber: '',
        corporationNumber: '',
        corporateName: '',
        location: '',
        corporationType: corporationTypeType.Type1
      }
    })

    const facilityItems = ref<FacilityInfoUiItem[]>([])
    let maxDisplayOrderBeforeUpdate = 0
    const isAllValid = computed(() => {
      const allCount = facilityItems.value.length
      if (allCount === 0) {
        return true
      }
      return allCount === facilityItems.value.filter(i => i.isValid).length
    })

    // コンボボックスのみvalidateが走らない事がある為、変更時にvalidate()
    watch([() => values.prefectureNumber, () => values.corporationType], () => {
      validate()
    })

    onMounted(async () => {
      await callApiWrapper(requestError, async () => {
        await setCorporationInfo()
        await setFacilityInfo()
        await validate()
      })
    })

    // 通知領域の非表示
    const onDismissAlert = () => {
      isSucceedRequest.value = false
      requestError.value = null
    }

    /**
     * データ取得（法人情報）
     */
    const setCorporationInfo = async () => {
      const data = await repository.getCorporationInfo()
      values.corporateName = data.corporateName
      values.location = data.location
      values.medicalCorporationNumber = data.medicalCorporationNumber
      values.prefectureNumber = data.prefectureNumber
      values.corporationNumber = data.corporationNumber
      values.corporationType = data.corporationType
    }

    /**
     * データ取得（施設情報）
     */
    const setFacilityInfo = async () => {
      const data = await repository.getFacilityInfo()
      facilityItems.value = data.map(value => ({ item: value, isValid: true }))
      maxDisplayOrderBeforeUpdate = facilityItems.value.length
    }

    /**
     * 登録（法人情報）
     */
    const registerCorporationInfo = async () => {
      onDismissAlert()
      loading.value = true
      await callApiWrapper(requestError, async () => {
        await repository.saveCorporationInfo({
          corporateName: values.corporateName,
          location: values.location,
          medicalCorporationNumber: values.medicalCorporationNumber,
          prefectureNumber: values.prefectureNumber,
          corporationNumber: values.corporationNumber,
          corporationType: values.corporationType
        })
        isSucceedRequest.value = true
      })
      loading.value = false
    }

    /**
     * 登録（施設情報）
     */
    const registerFacilityInfo = async () => {
      loading.value = true
      onDismissAlert()

      await callApiWrapper(requestError, async () => {
        const params = facilityItems.value.map(v => v.item)
        await repository.saveFacilityInfo(params)
        await setFacilityInfo()
        isSucceedRequest.value = true
      })
      loading.value = false
    }

    /**
     * 施設を追加
     */
    const addFacilityItem = () => {
      facilityItems.value.push({
        isValid: false,
        item: {
          facilityKey: makeRandomId(),
          displayOrder: facilityItems.value.length + 1,
          hospitalClinicKbn: '',
          prefectureCode: '',
          hasBedOutpatientMgmtNumber: '',
          bedOutpatientMgmtNumber: '',
          hasMedicalInstitutionCode: '',
          medicalInstitutionCode: '',
          hospitalClinicName: '',
          executiveCount: null,
          staffCount: null,
          prefecture: '',
          municipality: '',
          townArea: '',
          secondaryMedicalArea: '',
          taxAccountingMethod: '',
          primarySpecialty1: '',
          primarySpecialty2: '',
          primarySpecialty3: '',
          hasBedFunctionReport: '',
          outputSetting: '',
          bmnGroup: ''
        }
      })
    }

    /**
     * 上に移動（施設情報）
     */
    const upItem = (item: FacilityInfoUiItem) => {
      const index = facilityItems.value.findIndex(i => i.item.facilityKey === item.item.facilityKey)
      const currentItem = facilityItems.value.splice(index, 1)
      facilityItems.value.splice(index - 1, 0, currentItem[0])
    }

    /**
     * 下に移動（施設情報）
     */
    const downItem = (item: FacilityInfoUiItem) => {
      const index = facilityItems.value.findIndex(i => i.item.facilityKey === item.item.facilityKey)
      const currentItem = facilityItems.value.splice(index, 1)
      facilityItems.value.splice(index + 1, 0, currentItem[0])
    }

    /**
     * 削除（施設情報）
     */
    const trashItem = (item: FacilityInfoUiItem) => {
      const index = facilityItems.value.findIndex(i => i.item.facilityKey === item.item.facilityKey)
      facilityItems.value.splice(index, 1)
    }

    /**
     * 子コンポーネントによる施設情報の変更内容をfacilityItemsに反映
     */
    const onChangeInput = (item: FacilityInfoUiItem) => {
      const index = facilityItems.value.findIndex(i => i.item.facilityKey === item.item.facilityKey)
      facilityItems.value[index] = item
    }

    /**
     * 重複ない施設キー用の文字列を返す
     */
    const makeRandomId = () => {
      let newKey: string
      do {
        newKey = randomString(12)
      } while (facilityItems.value.filter(i => i.item.facilityKey === newKey).length)
      return newKey
    }

    return {
      requestError,
      loading,
      isSucceedRequest,
      errors,
      values,
      meta,
      validate,
      facilityItems,
      isAllValid,
      prefectures,
      corporationTypes,
      onDismissAlert,
      registerCorporationInfo,
      registerFacilityInfo,
      addFacilityItem,
      upItem,
      downItem,
      trashItem,
      onChangeInput
    }
  }
})
